import React, { useState, useEffect, useRef, useCallback } from 'react';
import VideoPlayer from '@/components/VideoPlayer';
import './index.scss';
// import { useWhyDidYouUpdate } from '@/utils/hooks/useWhyDidYouUpdate';

type AnyFunc = (...args: any[]) => any;

interface VideoPlayerPassthroughProps {
  videoId: 0 | 1 | 2 | 3 | 500 | 501 | 502 | 503 | 6; // 前主/右主/后主/左主/前备/右备/后备/左备/红绿灯
  direction?: string | React.ReactNode;
  vehicleName?: string;
  warning?: boolean;
  takeOverBySelf?: boolean;
  needSendData: boolean;
  userName?: string;
  showOpenBtn?: boolean;
  className?: string;
  getVideoBitRateInfo?: AnyFunc;
  openVideo?: AnyFunc;
}

interface MainBackupVideoPlayerProps extends VideoPlayerPassthroughProps {
  mainUrl: string;
  slaveUrl: string;
  slaveVideoId: 0 | 1 | 2 | 3 | 500 | 501 | 502 | 503 | 6; // 前主/右主/后主/左主/前备/右备/后备/左备/红绿灯
  // 加载优先级控制
  shouldLoad?: boolean; // 是否允许加载
  onVideoReady?: () => void; // 视频加载成功回调
  onVideoTimeout?: () => void; // 视频加载超时回调
}

const MainBackupVideoPlayer = (props: MainBackupVideoPlayerProps) => {
  // useWhyDidYouUpdate('MainBackupVideoPlayer', props);
  const {
    mainUrl,
    slaveUrl,
    direction,
    slaveVideoId,
    videoId,
    shouldLoad = true,
    onVideoReady,
    onVideoTimeout,
    ...restProps
  } = props;

  const SWITCH_THRESHOLD_MS = 100;
  const SWITCH_CONFIRM_COUNT = 3;
  const SWITCH_COOLDOWN_MS = 200;
  const latestFrameCurrentTimes = useRef({ main: 0, slave: 0 });
  const isLoadCompleteRef = useRef(false); // 标记是否已经加载完成（成功或超时）
  const [showMain, setShowMain] = useState(true);
  const showMainRef = useRef(true);
  const leaderRef = useRef<'main' | 'slave' | null>(null);
  const leaderCountRef = useRef(0);
  const lastSwitchAtRef = useRef(0);

  useEffect(() => {
    showMainRef.current = showMain;
  }, [showMain]);

  useEffect(() => {
    latestFrameCurrentTimes.current = { main: 0, slave: 0 };
    isLoadCompleteRef.current = false;
    leaderRef.current = null;
    leaderCountRef.current = 0;
    lastSwitchAtRef.current = 0;
    showMainRef.current = true;
    setShowMain(true);
  }, [mainUrl, slaveUrl, videoId]);

  const maybeSwitchPlayer = useCallback(() => {
    const { main, slave } = latestFrameCurrentTimes.current;
    if (main === 0 || slave === 0) {
      return;
    }

    let leader: 'main' | 'slave' | null = null;
    if (main - slave > SWITCH_THRESHOLD_MS) {
      leader = 'main';
    } else if (slave - main > SWITCH_THRESHOLD_MS) {
      leader = 'slave';
    }

    if (!leader) {
      leaderRef.current = null;
      leaderCountRef.current = 0;
      return;
    }

    if (leaderRef.current === leader) {
      leaderCountRef.current += 1;
    } else {
      leaderRef.current = leader;
      leaderCountRef.current = 1;
    }

    const shouldShowMain = leader === 'main';
    if (showMainRef.current === shouldShowMain) {
      return;
    }

    const now = Date.now();
    if (
      leaderCountRef.current >= SWITCH_CONFIRM_COUNT &&
      now - lastSwitchAtRef.current >= SWITCH_COOLDOWN_MS
    ) {
      lastSwitchAtRef.current = now;
      showMainRef.current = shouldShowMain;
      setShowMain(shouldShowMain);
      leaderRef.current = null;
      leaderCountRef.current = 0;
    }
  }, []);

  const handleFrameCurrentTime = useCallback(
    (type: 'main' | 'slave', frameTime: number) => {
      latestFrameCurrentTimes.current[type] = frameTime;

      if (!shouldLoad || isLoadCompleteRef.current) {
        maybeSwitchPlayer();
        return;
      }

      const mainFrameTime = latestFrameCurrentTimes.current.main;
      const slaveFrameTime = latestFrameCurrentTimes.current.slave;

      if (mainFrameTime > 0 || slaveFrameTime > 0) {
        isLoadCompleteRef.current = true;
        onVideoReady?.();
      }

      maybeSwitchPlayer();
    },
    [maybeSwitchPlayer, onVideoReady, shouldLoad]
  );

  // 视频加载超时检测（350ms）
  useEffect(() => {
    if (!shouldLoad) {
      return;
    }

    // 重置加载完成标记
    isLoadCompleteRef.current = false;

    const timeoutTimer = setTimeout(() => {
      // 如果已经加载完成，不再执行超时逻辑
      if (isLoadCompleteRef.current) {
        return;
      }

      // 350ms 后主备都未加载成功，触发超时回调
      const mainFrameTime = latestFrameCurrentTimes.current.main;
      const slaveFrameTime = latestFrameCurrentTimes.current.slave;

      if (mainFrameTime === 0 && slaveFrameTime === 0) {
        isLoadCompleteRef.current = true;
        onVideoTimeout?.();
      }
    }, 350);

    return () => clearTimeout(timeoutTimer);
  }, [shouldLoad, onVideoTimeout, videoId]);
  const onMainFrameUpdate = useCallback(
    (ts: number) => handleFrameCurrentTime('main', ts),
    [handleFrameCurrentTime]
  );

  const onSlaveFrameUpdate = useCallback(
    (ts: number) => handleFrameCurrentTime('slave', ts),
    [handleFrameCurrentTime]
  );

  return (
    <div className={`main-backup-video-container ${props.className}`}>
      {shouldLoad && (
        <div
          className={`player-wrapper ${
            showMain ? 'player-wrapper--visible' : 'player-wrapper--hidden'
          }`}
        >
          <VideoPlayer
            {...restProps}
            direction={direction}
            key={`main-${props.videoId}`}
            videoUrl={mainUrl}
            videoId={videoId}
            showVideoId={showMain ? videoId : slaveVideoId}
            onFrameUpdate={onMainFrameUpdate}
          />
        </div>
      )}

      {shouldLoad && slaveUrl && (
        <div
          className={`player-wrapper ${
            showMain ? 'player-wrapper--hidden' : 'player-wrapper--visible'
          }`}
        >
          <VideoPlayer
            {...restProps}
            direction={direction}
            key={`slave-${props.videoId}`}
            videoUrl={slaveUrl}
            videoId={slaveVideoId}
            showVideoId={showMain ? videoId : slaveVideoId}
            onFrameUpdate={onSlaveFrameUpdate}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(MainBackupVideoPlayer);
